import cn from 'classnames'
import { selectCurrentLanguage, useAppSelector } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { type ReactNode, useEffect, useState } from 'react'
import Select, { type CSSObjectWithLabel, components, type MenuPlacement, type MenuPosition } from 'react-select'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import './SelectInput.scss'

interface Props {
  id?: string
  name?: string
  async?: boolean
  error?: string
  label?: string
  theme?: string
  value?: string | number
  options?: any //array
  onChange?: (name: string, value: string) => void
  dataTest?: string
  disabled?: boolean
  required?: boolean
  className?: string
  creatable?: boolean
  hideLabel?: boolean
  applyStyles?: any
  loadOptions?: any
  placeholder?: string
  menuPosition?: MenuPosition
  hideRequired?: boolean
  disableSearch?: boolean
  hideErrorLine?: boolean
  menuPlacement?: MenuPlacement
  indicatorIcon?: ReactNode
  loadingMessage?: string
  onCreateOption?: (value: string) => void
  preselectValue?: string | number
  parentClassName?: string
  noOptionsMessage?: string
  formatCreateLabel?: (input: string) => ReactNode
  valueIsObject?: boolean
}

function SelectInput({
  id,
  name,
  async,
  error,
  label,
  theme,
  value,
  options,
  onChange,
  dataTest,
  disabled,
  required = false,
  className,
  creatable,
  hideLabel,
  applyStyles,
  loadOptions,
  placeholder,
  menuPosition,
  hideRequired,
  disableSearch,
  hideErrorLine,
  menuPlacement,
  indicatorIcon,
  loadingMessage,
  onCreateOption,
  preselectValue,
  parentClassName,
  noOptionsMessage,
  formatCreateLabel,
  valueIsObject
}: Props) {
  const [selectedOption, setSelectedOption] = useState(null)
  const [translatedOptions, setTranslatedOptions] = useState([])

  function getPlaceholder() {
    if (placeholder?.length) return placeholder
    return (required && !hideRequired ? '* ' : '') + label
  }

  function handleCreate(value: string) {
    onCreateOption && onCreateOption(value)
    setSelectedOption({ label: value, value })
  }

  function handleChange(selectedOption: any) {
    onChange && onChange(name, selectedOption.value)
    setSelectedOption(selectedOption)
  }

  function isLabelVisible() {
    if (label?.length) {
      return value != null && value.toString().length > 0
    }
    return false
  }

  const selectedLanguage = useAppSelector(selectCurrentLanguage)

  function prepareIndicatorIcon() {
    if (indicatorIcon) {
      // noinspection JSUnusedGlobalSymbols
      return {
        DropdownIndicator: (props: any) => (
          <components.DropdownIndicator {...props}>{indicatorIcon}</components.DropdownIndicator>
        )
      }
    }
    return null
  }

  useEffect(() => {
    const filteredOptions = options?.map((option: any) => ({
      ...option,
      label: option?.localizationKey ? tr(option?.localizationKey) : option.label
    }))

    setTranslatedOptions(filteredOptions)
  }, [options, selectedLanguage])

  const defaultStyles = {
    menu: (provided: CSSObjectWithLabel) => ({
      ...provided,
      fontSize: '1.8rem',
      border: 'none !important',
      backgroundColor: '#ffffff !important'
    }),
    control: (provided: CSSObjectWithLabel) => ({
      ...provided,
      height: '5rem',
      borderRadius: '3px',
      borderColor: error && error.length > 0 ? 'transparent' : '#a3b8c2',
      borderBottomColor: error && error.length > 0 ? '#ff3131' : '#a3b8c2',
      boxShadow: 'none',
      backgroundColor: '#ffffff'
    }),
    placeholder: (provided: CSSObjectWithLabel) => ({
      ...provided,
      color: '#a3b8c2',
      fontSize: '1.8rem',
      fontStyle: 'normal',
      fontWeight: 'normal'
    }),
    valueContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      color: '#000000',
      fontSize: '1.8rem',
      fontWeight: '500',
      padding: '2px 0'
    }),
    singleValue: (provided: CSSObjectWithLabel) => ({
      ...provided,
      color: '#000000'
    }),
    option: (provided: CSSObjectWithLabel, { isFocused }: { isFocused: boolean }) => ({
      ...provided,
      color: isFocused ? '#ffffff' : '#000000',
      backgroundColor: isFocused ? '#000000' : 'transparent'
    })
  }

  useEffect(() => {
    if (!async && !creatable) {
      const option = translatedOptions?.find(item => item.value === value || item.value === value?.toString())
      setSelectedOption(option ?? null)
    }
    // eslint-disable-next-line
  }, [value, translatedOptions, selectedOption])

  useEffect(() => {
    if (preselectValue) {
      if (Array.isArray(options)) {
        const find = options.find(v => v.value.toString() === preselectValue.toString())
        if (find) {
          handleChange(find)
        }
      }
    }
    // eslint-disable-next-line
  }, [preselectValue, options])

  function addDataTest(Component: any, dataTest: string) {
    return (props: any) => (
      <Component {...props} innerProps={Object.assign({}, props.innerProps, { 'data-test': dataTest })} />
    )
  }

  return (
    <div className={cn('select-input', 'select-wrapper', parentClassName, theme)}>
      {!hideLabel ? (
        <label>
          {isLabelVisible() && required && !hideRequired && <span className='asterisk'>*</span>}
          {isLabelVisible() && <span>{label}</span>}
          {!isLabelVisible() && ' '}
        </label>
      ) : null}

      {async ? (
        <AsyncSelect
          id={id || name}
          name={name}
          isDisabled={disabled}
          placeholder={getPlaceholder()}
          className={cn('select-input', className, { error: error && error.length > 0 })}
          loadOptions={loadOptions}
          loadingMessage={() => loadingMessage}
          defaultOptions
          value={selectedOption}
          styles={applyStyles || defaultStyles}
          onChange={handleChange}
          noOptionsMessage={() => noOptionsMessage || 'No options'}
          required={required}
          components={{
            IndicatorSeparator: () => null,
            ...(dataTest?.length ? { SelectContainer: addDataTest(components.SelectContainer, dataTest) } : null)
          }}
          menuPlacement={menuPlacement || 'bottom'}
          menuPosition={menuPosition || 'absolute'}
        />
      ) : creatable ? (
        <CreatableSelect
          id={id || name}
          name={name}
          isDisabled={disabled}
          isSearchable={!disableSearch}
          placeholder={getPlaceholder()}
          className={cn('select-input', className, { error: error && error.length > 0 })}
          options={translatedOptions}
          value={selectedOption}
          styles={applyStyles || defaultStyles}
          onChange={handleChange}
          onCreateOption={handleCreate}
          noOptionsMessage={() => noOptionsMessage || 'No options'}
          required={required}
          components={{
            IndicatorSeparator: () => null,
            ...(dataTest?.length ? { SelectContainer: addDataTest(components.SelectContainer, dataTest) } : null),
            ...prepareIndicatorIcon()
          }}
          formatCreateLabel={formatCreateLabel}
          menuPlacement={menuPlacement || 'bottom'}
          menuPosition={menuPosition || 'absolute'}
        />
      ) : (
        <Select
          id={id || name}
          name={name}
          isDisabled={disabled}
          isSearchable={!disableSearch}
          placeholder={getPlaceholder()}
          className={cn('select-input', className, { error: error && error.length > 0 })}
          options={translatedOptions}
          value={valueIsObject ? selectedOption || { value: value, label: value } : selectedOption}
          styles={applyStyles || defaultStyles}
          onChange={handleChange}
          noOptionsMessage={() => noOptionsMessage || 'No options'}
          required={required}
          components={{
            IndicatorSeparator: () => null,
            ...(dataTest?.length ? { SelectContainer: addDataTest(components.SelectContainer, dataTest) } : null),
            ...prepareIndicatorIcon()
          }}
          menuPlacement={menuPlacement || 'bottom'}
          menuPosition={menuPosition || 'absolute'}
        />
      )}

      {!hideErrorLine || error ? <span className='error-message'>{error}</span> : null}
    </div>
  )
}

export default SelectInput
